import React, { useMemo } from "react";
import DataTable from "react-data-table-component";

const SelectAllHeader = ({
  saveSelection,
  tableData,
  pageSelectionSet,
  onSelectAllToggle,
}) => {
  const isGlobalAll = saveSelection === "selected All";
  const allOnPageSelected =
    tableData.length > 0 &&
    tableData.every((row) => pageSelectionSet.has(String(row.id)));
  const checked = isGlobalAll || allOnPageSelected;
  const indeterminate =
    !isGlobalAll && pageSelectionSet.size > 0 && !allOnPageSelected;

  return (
    <input
      type="checkbox"
      className="qr-row-checkbox"
      aria-label="Select all students"
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      onChange={(e) => {
        e.stopPropagation();
        onSelectAllToggle();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const RowCheckbox = ({ row, pageSelectionSet, onRowToggle }) => {
  const isSelected = pageSelectionSet.has(String(row.id));
  return (
    <input
      type="checkbox"
      className="qr-row-checkbox"
      aria-label="Select row"
      checked={isSelected}
      onChange={(e) => {
        e.stopPropagation();
        onRowToggle(row, e.target.checked);
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const GpaCell = ({ value }) => {
  const val = parseFloat(value);
  const bg = isNaN(val)
    ? "#f9fafb"
    : val >= 3.5
    ? "#f0fdf4"
    : val >= 3.0
    ? "#fffbeb"
    : val >= 2.5
    ? "#fef2f2"
    : "#f9fafb";
  const color = isNaN(val)
    ? "#6b7280"
    : val >= 3.5
    ? "#059669"
    : val >= 3.0
    ? "#d97706"
    : val >= 2.5
    ? "#dc2626"
    : "#6b7280";
  return (
    <div
      style={{
        backgroundColor: bg,
        color,
        padding: "6px 10px",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      {isNaN(val) ? "N/A" : val.toFixed(2)}
    </div>
  );
};

const YesNoCell = ({ value, yesStyle, noStyle }) => {
  const v = (value || "").toString().toLowerCase();
  const yes = v === "yes" || v === "true" || v === "1";
  return (
    <span
      style={{
        backgroundColor: yes ? yesStyle.bg : noStyle.bg,
        color: yes ? yesStyle.color : noStyle.color,
        padding: "4px 8px",
        borderRadius: "10px",
        fontSize: "12px",
        fontWeight: 600,
      }}
    >
      {yes ? "Yes" : value ? "No" : "N/A"}
    </span>
  );
};

const TagListCell = ({ value, limit, emptyLabel, tagStyle }) => {
  const items = Array.isArray(value)
    ? value
    : value && typeof value === "string"
    ? value.split(",")
    : [];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
      {items.length > 0 ? (
        items.slice(0, limit).map((item, idx) => (
          <span key={idx} style={tagStyle}>
            {item?.trim()}
          </span>
        ))
      ) : (
        <span style={{ color: "#9ca3af", fontSize: "12px", fontStyle: "italic" }}>
          {emptyLabel}
        </span>
      )}
    </div>
  );
};

const tableCustomStyles = {
  table: { style: { width: "max-content", minWidth: "100%" } },
  tableWrapper: {
    style: {
      display: "block",
      width: "max-content",
      minWidth: "100%",
      overflow: "visible",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderBottom: "2px solid #e2e8f0",
      minHeight: "44px",
      width: "max-content",
      minWidth: "100%",
    },
  },
  headCells: {
    style: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#374151",
      textTransform: "none",
      letterSpacing: "normal",
      padding: "8px 12px",
      whiteSpace: "nowrap",
      flexShrink: 0,
      borderBottom: "2px solid #e2e8f0",
    },
  },
  rows: {
    style: {
      minHeight: "56px",
      fontSize: "13px",
      borderBottom: "none",
      width: "max-content",
      minWidth: "100%",
    },
  },
  cells: {
    style: {
      padding: "8px 12px",
      flexShrink: 0,
      borderBottom: "1px solid #f1f5f9",
      backgroundColor: "inherit",
    },
  },
  pagination: { style: { display: "none" } },
};

const QueryResultTableInternational = ({
  tableData,
  saveSelection,
  pageSelectionSet,
  onRowToggle,
  onSelectAllToggle,
  sortField,
  sortDirection,
  onSort,
  selectedCount,
}) => {
  const columns = useMemo(
    () => [
      {
        name: (
          <SelectAllHeader
            saveSelection={saveSelection}
            tableData={tableData}
            pageSelectionSet={pageSelectionSet}
            onSelectAllToggle={onSelectAllToggle}
          />
        ),
        width: "52px",
        minWidth: "52px",
        sortable: false,
        cell: (row) => (
          <RowCheckbox
            row={row}
            pageSelectionSet={pageSelectionSet}
            onRowToggle={onRowToggle}
          />
        ),
      },
      {
        name: "Email",
        selector: (row) => row["0"] || "",
        sortField: "0",
        id: "0",
        sortable: true,
        minWidth: "260px",
        grow: 2,
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#eff6ff",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "12px",
              color: "#1e40af",
              fontWeight: 500,
            }}
          >
            {row["0"] || "No email"}
          </span>
        ),
      },
      {
        name: "First name",
        selector: (row) => row["1"] || "",
        sortField: "1",
        id: "1",
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#059669", fontSize: "14px" }}>
            {row["1"] || "-"}
          </span>
        ),
      },
      {
        name: "Last name",
        selector: (row) => row["2"] || "",
        sortField: "2",
        id: "2",
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <span style={{ fontWeight: 600, color: "#059669", fontSize: "14px" }}>
            {row["2"] || "-"}
          </span>
        ),
      },
      {
        name: "Gender",
        selector: (row) => row["3"] || "",
        sortField: "3",
        id: "3",
        sortable: true,
        minWidth: "110px",
        cell: (row) => (
          <div
            style={{
              backgroundColor:
                row["3"] === "Male"
                  ? "#fef3f2"
                  : row["3"] === "Female"
                  ? "#f0f9ff"
                  : "#f9fafb",
              color:
                row["3"] === "Male"
                  ? "#dc2626"
                  : row["3"] === "Female"
                  ? "#2563eb"
                  : "#6b7280",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {row["3"] || "Not specified"}
          </div>
        ),
      },
      {
        name: "USA Equivalent GPA",
        selector: (row) => row["4"] || "",
        sortField: "4",
        id: "4",
        sortable: true,
        minWidth: "150px",
        cell: (row) => <GpaCell value={row["4"]} />,
      },
      {
        name: "Actual GPA",
        selector: (row) => row["5"] || "",
        sortField: "5",
        id: "5",
        sortable: true,
        minWidth: "120px",
        cell: (row) => <GpaCell value={row["5"]} />,
      },
      {
        name: "Country",
        selector: (row) => row["6"] || "",
        sortField: "6",
        id: "6",
        sortable: true,
        minWidth: "120px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#f0f9ff",
              color: "#0369a1",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {row["6"] || "-"}
          </span>
        ),
      },
      {
        name: "State",
        selector: (row) => row["7"] || "",
        sortField: "7",
        id: "7",
        sortable: true,
        minWidth: "110px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#f9fafb",
              color: "#374151",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              border: "1px solid #e5e7eb",
              fontWeight: 600,
            }}
          >
            {row["7"] || "-"}
          </span>
        ),
      },
      {
        name: "Board Certification",
        selector: (row) => row["8"] || "",
        sortField: "8",
        id: "8",
        sortable: false,
        minWidth: "150px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#eef2ff",
              color: "#4338ca",
              padding: "4px 8px",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {row["8"] || "N/A"}
          </span>
        ),
      },
      {
        name: "Current School Name",
        selector: (row) => row["9"] || "",
        sortField: "9",
        id: "9",
        sortable: true,
        minWidth: "220px",
        grow: 2,
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#f9fafb",
              padding: "4px 8px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              fontSize: "13px",
              color: "#374151",
              fontWeight: 500,
            }}
          >
            {row["9"] || "No school specified"}
          </span>
        ),
      },
      {
        name: "English Test",
        selector: (row) => row["10"] || "",
        sortField: "10",
        id: "10",
        sortable: false,
        minWidth: "130px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#fdf2f8",
              color: "#be185d",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {row["10"] || "-"}
          </span>
        ),
      },
      {
        name: "Planned Enrollment Date",
        selector: (row) => row["11"] || "",
        sortField: "11",
        id: "11",
        sortable: false,
        minWidth: "180px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#fff7ed",
              color: "#9a3412",
              padding: "4px 10px",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {row["11"] || "-"}
          </span>
        ),
      },
      {
        name: "Desired Degree",
        selector: (row) => row["12"] || "",
        sortField: "12",
        id: "12",
        sortable: true,
        minWidth: "140px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#ecfeff",
              color: "#0e7490",
              padding: "4px 8px",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {row["12"] || "-"}
          </span>
        ),
      },
      {
        name: "Project Work Seminar",
        selector: (row) => row["13"] || "",
        sortField: "13",
        id: "13",
        sortable: false,
        minWidth: "160px",
        cell: (row) => (
          <YesNoCell
            value={row["13"]}
            yesStyle={{ bg: "#f0fdf4", color: "#059669" }}
            noStyle={{ bg: "#fef2f2", color: "#dc2626" }}
          />
        ),
      },
      {
        name: "Social Leadership Activities",
        selector: (row) => row["14"] || "",
        sortField: "14",
        id: "14",
        sortable: false,
        minWidth: "220px",
        cell: (row) => (
          <TagListCell
            value={row["14"]}
            limit={3}
            emptyLabel="No activities"
            tagStyle={{
              backgroundColor: "#fef3f2",
              color: "#dc2626",
              padding: "2px 6px",
              borderRadius: "8px",
              fontSize: "11px",
              fontWeight: 500,
            }}
          />
        ),
      },
      {
        name: "Relevant Degree",
        selector: (row) => row["15"] || "",
        sortField: "15",
        id: "15",
        sortable: false,
        minWidth: "140px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#f3f4f6",
              color: "#374151",
              padding: "4px 8px",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: 600,
              border: "1px solid #e5e7eb",
            }}
          >
            {row["15"] || "-"}
          </span>
        ),
      },
      {
        name: "GRE Above 330",
        selector: (row) => row["16"] || "",
        sortField: "16",
        id: "16",
        sortable: false,
        minWidth: "130px",
        cell: (row) => (
          <YesNoCell
            value={row["16"]}
            yesStyle={{ bg: "#ecfdf5", color: "#047857" }}
            noStyle={{ bg: "#fef2f2", color: "#b91c1c" }}
          />
        ),
      },
      {
        name: "Interests",
        selector: (row) => row["17"] || "",
        sortField: "17",
        id: "17",
        sortable: false,
        minWidth: "280px",
        grow: 2,
        cell: (row) => (
          <TagListCell
            value={row["17"]}
            limit={4}
            emptyLabel="No interests"
            tagStyle={{
              backgroundColor: "#fef7ff",
              color: "#9333ea",
              padding: "2px 6px",
              borderRadius: "8px",
              fontSize: "11px",
              fontWeight: 500,
            }}
          />
        ),
      },
    ],
    [tableData, saveSelection, pageSelectionSet, onRowToggle, onSelectAllToggle]
  );

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => pageSelectionSet.has(String(row.id)),
        style: { backgroundColor: "#e0f2fe" },
      },
    ],
    [pageSelectionSet]
  );

  return (
    <div className="qr-rdt-wrapper">
      {selectedCount > 0 && (
        <div className="qr-selection-bar">
          {selectedCount} row{selectedCount !== 1 ? "s" : ""} selected
        </div>
      )}
      <div className="qr-table-scroll">
        <DataTable
          className="result-table"
          columns={columns}
          data={tableData}
          keyField="id"
          sortServer
          onSort={onSort}
          {...(sortField
            ? {
                defaultSortFieldId: sortField,
                defaultSortAsc: sortDirection === "asc",
              }
            : {})}
          pagination={false}
          customStyles={tableCustomStyles}
          conditionalRowStyles={conditionalRowStyles}
          dense
          persistTableHead
          noDataComponent={
            <div style={{ padding: "24px", color: "#6b7280" }}>No students found</div>
          }
        />
      </div>
    </div>
  );
};

export default QueryResultTableInternational;
